import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import {
  FileDto,
  FileId,
  PropertyDocumentDto,
  PropertyDocumentStatus,
  PropertyDto,
  PropertyId,
} from '@workspace/data-types';
import { AlertSnackBarService } from '@workspace/ng-aura';
import { ApiService } from '@workspace/ng-http';
import { catchError, of, tap } from 'rxjs';
import { EtxeEnumsNiceLabelUtils } from '../../../../utils/etxe-enums-nice-label.utils';
import {
  PropertyDocumentRejectDialogComponent,
  PropertyDocumentRejectDialogData,
} from '../reject-dialog/property-document-reject-dialog.component';

@Component({
  selector: 'etxe-property-document-admin-status-menu',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatMenuModule,
    MatDialogModule,
  ],
  templateUrl: './property-document-admin-status-menu.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PropertyDocumentAdminStatusMenuComponent {
  private readonly api = inject(ApiService);
  private readonly alertSnackbar = inject(AlertSnackBarService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly dialog = inject(MatDialog);

  doc = input.required<PropertyDocumentDto>();
  propertyId = input.required<PropertyId>();
  propertyUpdated = output<PropertyDto>();

  isUpdating = signal<boolean>(false);
  PropertyDocumentStatus = PropertyDocumentStatus;

  private getFileId(): FileId | null {
    const file = this.doc().file;
    if (!file) return null;
    if (typeof file === 'string' || (typeof file === 'object' && !('_id' in file))) {
      return file as FileId;
    }
    return ((file as FileDto)._id as FileId) || null;
  }

  private getFileName(): string {
    const file = this.doc().file;
    if (!file) return 'Document';
    if (typeof file === 'object' && 'filename' in file) {
      return (file as FileDto).filename || 'Document';
    }
    return this.doc().label || 'Document';
  }

  onSelectStatus(status: PropertyDocumentStatus): void {
    if (status === PropertyDocumentStatus.REJECTED) {
      this.openRejectDialog();
    } else {
      this.submitStatusUpdate(status);
    }
  }

  private openRejectDialog(): void {
    const dialogRef = this.dialog.open<
      PropertyDocumentRejectDialogComponent,
      PropertyDocumentRejectDialogData,
      string | null
    >(PropertyDocumentRejectDialogComponent, {
      width: '520px',
      data: {
        documentName: this.getFileName(),
        documentType: this.doc().type,
        currentReason: this.doc().rejectionReason,
      },
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((reason) => {
        if (reason) {
          this.submitStatusUpdate(PropertyDocumentStatus.REJECTED, reason);
        }
      });
  }

  private submitStatusUpdate(status: PropertyDocumentStatus, rejectionReason?: string): void {
    const fileId = this.getFileId();
    const propId = this.propertyId();
    if (!fileId || !propId) return;

    this.isUpdating.set(true);

    this.api
      .business()
      .etxe()
      .admin()
      .properties()
      .patchDocumentStatus(propId, fileId, status, rejectionReason)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((updatedProperty) => {
          this.isUpdating.set(false);
          this.propertyUpdated.emit(updatedProperty);
          this.alertSnackbar.success({
            title: 'Statut du document mis à jour',
            message: `Le document est maintenant marqué comme : ${EtxeEnumsNiceLabelUtils.documentStatus(status)}`,
          });
        }),
        catchError((err) => {
          console.error('Failed to update document status:', err);
          this.isUpdating.set(false);
          this.alertSnackbar.error({
            title: 'Erreur',
            message: 'Impossible de mettre à jour le statut du document.',
          });
          return of(null);
        }),
      )
      .subscribe();
  }
}
